// @ts-strict-ignore
import { BehaviorSubject, Subscription } from 'rxjs';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { ConversationRB } from 'rainbow-web-sdk/models/conversation.model';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { BubbleConferenceServiceEvents, BubbleConferenceServiceRB } from 'rainbow-web-sdk/services/conference/bubbleConference.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { Service } from 'rainbow-web-sdk/services/service';
import { UtilitiesService } from '../utilities/utilities.service';
import { BubbleConferenceStatus } from '../../models/webConferenceSession.model';
import { $msg } from 'strophe.js';

export const SPEECH_RECOGNITION_SVC = 'SpeechRecognitionService';


export class SpeechRecognitionMessage {
    declare id: string;
    declare text: string;
    declare timeout: any;
    declare user: Contact;
    declare service: SpeechRecognitionService;


    public static create(id: string, text: string, user: Contact, service: SpeechRecognitionService): SpeechRecognitionMessage {
        const message = new SpeechRecognitionMessage();
        message.id = id;
        message.user = user;
        message.service = service;
        message.update(text);
        return message;
    }

    public update(text: string) {
        this.text = text;
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
            const messages = this.service.speechRecognitionMessages.value;
            const index = messages.findIndex((message: SpeechRecognitionMessage) => { return message.id === this.id; });
            messages.splice(index, 1);
            this.service.speechRecognitionMessages.next(messages);
        }, 10000);
    }
}
export interface ISpeechRecognitionService {
    init(type: string, stream: MediaStream, roomJid?: string): Promise<void>;
    stop(): void;
    startTranscription(): void;
    stopTranscription(): void;
}

export class SpeechRecognitionModelInfo {
    public _selected: boolean = false;
    public static create(name: string, lang: string, url: string, bufferSize: number = 4096) { return new SpeechRecognitionModelInfo(name, lang, url, bufferSize); }
    private constructor(public name: string, public lang: string, public url: string, public bufferSize: number) { }
}

export class SpeechRecognitionModel {
    public static create(name: string, modelsInfo: SpeechRecognitionModelInfo[]) { return new SpeechRecognitionModel(name, modelsInfo); }
    private constructor(public name: string, public modelsInfo: SpeechRecognitionModelInfo[]) { }
}

export class SpeechRecognitionTranscription {
    public messages: any[] = [];

    public reset(): void {
        this.messages = [];
    }

    public static create(): SpeechRecognitionTranscription {
        return new SpeechRecognitionTranscription();
    }

    public addMessage(user: Contact, text: string) {
        this.messages.push({ timestamp: new Date(), user, text });
    }
}

export class SpeechRecognitionService extends Service implements ISpeechRecognitionService {

    private stream: MediaStream | null = null;
    private websocket: WebSocket | null = null;
    private audioContext: AudioContext | null = null;
    private logger: LoggerService = LoggerService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private webConferenceService: BubbleConferenceServiceRB = BubbleConferenceServiceRB.getInstance();
    private roomJid: string | null = null;
    private models: SpeechRecognitionModel[] = [];
    private currentModel: SpeechRecognitionModelInfo | null = null;
    private xmppHandler: any = null;
    private subscription: Subscription = null;
    private type: string = null;
    private buffer: any[] = [];
    public transcription: BehaviorSubject<SpeechRecognitionTranscription>;
    public transcriptionOn: BehaviorSubject<boolean>;
    public speechRecognitionMessages: BehaviorSubject<SpeechRecognitionMessage[]>;
    public peer?: string;
    public started: boolean = false;
    public muted: boolean = false;


    /**
     * @function getInstance
     * @static
     * @private
     * @description Get the SpeechRecognitionService singleton
     * @returns {SpeechRecognitionService} the SpeechRecognitionService singleton
     */
    public static getInstance() {
        return Service.create(SPEECH_RECOGNITION_SVC, SpeechRecognitionService);
    }

    public static build() {
        return new SpeechRecognitionService();
    }

    private constructor() {
        super();
        this.speechRecognitionMessages = new BehaviorSubject<SpeechRecognitionMessage[]>([]);
        this.transcription = new BehaviorSubject<SpeechRecognitionTranscription>(SpeechRecognitionTranscription.create());
        this.transcriptionOn = new BehaviorSubject<boolean>(false);

        if (this.xmppService.connected) this.subscribeToASRMessages();
        this.xmppService.subscribeToConnectionEvents((event: RBEvent) => {
            if (event.name === 'ON_XMPP_CONNECTED') { this.subscribeToASRMessages(); }
            if (event.name === 'ON_XMPP_DISCONNECTED' && event.data.reason === 'LOGOUT') { /* */ }
        });
    }

    public async init(type: string, stream: MediaStream, roomJid?: string, modelInfo?: SpeechRecognitionModelInfo): Promise<void> {
        try {
            const bufferSize = modelInfo ? modelInfo.bufferSize : 4096;
            this.logger.info(`[SpeechRecognitionService] init ${type} -- ${roomJid} -- bufferSize : ${bufferSize}`);
            this.type = type;
            this.roomJid = roomJid;
            await this.getModels();
            await this.createWebSocket();
            this.stream = stream;
            this.audioContext = new AudioContext({ sampleRate: 16000 });
            const audioInput = this.audioContext.createMediaStreamSource(stream);
            const recorder = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
            recorder.onaudioprocess = (event) => { this.recorderProcess(event); };
            audioInput.connect(recorder);
            recorder.connect(this.audioContext.destination);

            if (type === 'room') {
                this.subscription = this.webConferenceService.eventSubscribe((event: RBEvent) => {
                    if (event.name === BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE) {
                        if (event.data.bubbleConference?.subtitlesEnable && event.data.status === BubbleConferenceStatus.ENDED) this.stop();
                    }
                });
            }

            this.started = true;
        }
        catch (error) {
            this.logger.error(`[SpeechRecognitionService] init -- FAILURE -- ${error.message}`);
            throw new Error(error.message);
        }
    }

    public stop(): void {
        if (this.started) {
            this.logger.info(`[SpeechRecognitionService] stop`);
            this.audioContext.close();
            this.websocket.send('{"eof": 1}');
            this.started = false;
            //if (this.xmppHandler) { this.xmppService.connection.deleteHandler(this.xmppHandler); this.xmppHandler = null; }
        }
        if (this.subscription) { this.subscription.unsubscribe(); }
    }

    public startTranscription(): void {
        this.transcription.value.reset();
        this.transcriptionOn.next(true);
    }

    public stopTranscription(): void {
        this.transcriptionOn.next(false);
    }

    public async getModels(): Promise<SpeechRecognitionModel[]> {
        if (this.models.length === 0) { await this.getModelsFromServer(); }
        return this.models;
    }

    public getCurrentModel(): SpeechRecognitionModelInfo { return this.currentModel; }

    public mute(mute: boolean): void {
        this.muted = mute;
    }

    private async getModelsFromServer(): Promise<void> {
        const frenchModels: SpeechRecognitionModelInfo[] = [
            SpeechRecognitionModelInfo.create('Model Vosk', 'french', 'wss://demo.openrainbow.org:666/asr-ws/fr-vosk', 8192),
            SpeechRecognitionModelInfo.create('Model Linto', 'french', 'wss://demo.openrainbow.org:666/asr-ws/fr'),
            SpeechRecognitionModelInfo.create('Model ALE', 'french', 'wss://demo.openrainbow.org:666/asr-ws/fr-ale'),
        ];

        const englishModels: SpeechRecognitionModelInfo[] = [
            SpeechRecognitionModelInfo.create('Model Danzu old', 'english', 'wss://demo.openrainbow.org:666/asr-ws/en-nn'),
            SpeechRecognitionModelInfo.create('Model Linto', 'english', 'wss://demo.openrainbow.org:666/asr-ws/en'),
            SpeechRecognitionModelInfo.create('Model Vosk 21', 'english', 'wss://demo.openrainbow.org:666/asr-ws/en-vosk-21', 8192),
            SpeechRecognitionModelInfo.create('Model Vosk 22', 'english', 'wss://demo.openrainbow.org:666/asr-ws/en-vosk-22', 8192),
            SpeechRecognitionModelInfo.create('Model Vosk Aspire', 'english', 'wss://demo.openrainbow.org:666/asr-ws/en-vosk-aspire', 8192),
            SpeechRecognitionModelInfo.create('Whisper-live', 'english', 'wss://demo.openrainbow.org:666/asr-ws/whisper', 8192),
            SpeechRecognitionModelInfo.create('Gladia', 'english', 'wss://api.gladia.io/audio/text/audio-transcription ', 8192),
            //SpeechRecognitionModelInfo.create('Whisper-streaming', 'english', 'ws://10.21.91.11:43007')
        ];

        const germanModels: SpeechRecognitionModelInfo[] = [
            SpeechRecognitionModelInfo.create('Model Vosk', 'german', 'wss://demo.openrainbow.org:666/asr-ws/de-vosk', 8192)
        ];

        const russianModels: SpeechRecognitionModelInfo[] = [
            SpeechRecognitionModelInfo.create('Model Vosk', 'russian', 'wss://demo.openrainbow.org:666/asr-ws/ru-vosk', 8192)
        ];

        this.models.push(SpeechRecognitionModel.create('subtitleFrench', frenchModels));
        this.models.push(SpeechRecognitionModel.create('subtitleEnglish', englishModels));
        this.models.push(SpeechRecognitionModel.create('subtitleGerman', germanModels));
        this.models.push(SpeechRecognitionModel.create('subtitleRussian', russianModels));

        this.currentModel = this.models[1].modelsInfo[5];
        this.currentModel._selected = true;
        this.logger.info(`[SpeechRecognitionService] getModelsFromServer -- SUCCESS -- current model : ${this.currentModel.lang} - ${this.currentModel.name} - ${this.currentModel.url}`);
    }

    public async setModel(modelInfo: SpeechRecognitionModelInfo): Promise<void> {
        this.logger.info(`[SpeechRecognitionService] setModel ${modelInfo.lang} - ${modelInfo.name} - ${modelInfo.url}`);
        this.currentModel = modelInfo;
        if (this.started) {
            this.stop();
            await this.init(this.type, this.stream, this.roomJid, modelInfo);
        }
    }

    private createWebSocket(): Promise<void> {
        const gladiaKey = '9ca8b0e6-717c-4b41-93df-71864af8627e';
        const ERROR_KEY = "error";
        const TYPE_KEY = "type";
        const TRANSCRIPTION_KEY = "transcription";
        const LANGUAGE_KEY = "language";
        const SAMPLE_RATE = 16_000;

        return new Promise<void>((resolve: any, reject: any) => {
            this.logger.info(`[SpeechRecognitionService] createWebSocket -- ${this.currentModel.lang} -- ${this.currentModel.name} -- ${this.currentModel.url}`);
            this.websocket = new WebSocket(this.currentModel.url);

            this.websocket.onopen = () => {
                this.logger.info(`[SpeechRecognitionService] createWebSocket -- ${this.currentModel.lang} -- ${this.currentModel.name} -- ${this.currentModel.url} -- SUCCESS`);
                if (this.currentModel.name.startsWith('Whisper')) {
                    const uid = UtilitiesService.getInstance().randomString();
                    const config = JSON?.stringify({ uid, multilingual: true, language: "en", task: "translate" });

                    /*const config = JSON?.stringify({
                        uid,
                        task: "translate",
                        multilingual: true,
                        language: "en",
                        model: "large-v2",
                        use_vad: true
                    });*/
                    this.websocket.send(config);
                }
                else if (this.currentModel.name === 'Gladia') {
                    const configuration = {
                        x_gladia_key: gladiaKey,
                        language_behaviour: "automatic single language",
                        sample_rate: SAMPLE_RATE,
                        frames_format: "bytes"
                    };
                    const config = JSON?.stringify(configuration);
                    this.websocket.send(config);
                }
                else this.websocket.send('{"config": {"sample_rate":16000 }}');
                resolve();
            };

            this.websocket.onerror = () => {
                this.websocket.close();
                this.logger.info(`[SpeechRecognitionService] createWebSocket -- ${this.currentModel.lang} -- ${this.currentModel.name} -- ${this.currentModel.url} -- FAILURE`);
                reject(new Error(`websocket error`));
            };

            this.websocket.onclose = () => {
                this.logger.info(`[SpeechRecognitionService] webSocket is closed`);
            };

            this.websocket.onmessage = async (event: MessageEvent) => {
                const data = JSON.parse(event.data);
                if (this.currentModel.name.startsWith('Whisper')) {
                    const segments = data.segments;

                    /*console.error('------------------ AVANT ------------------------');
                    if (segments.length >= 1) console.error(`-- segment0 -- ${segments[0].start} -- ${segments[0].text}`);
                    if (segments.length === 2) console.error(`-- segment1 -- ${segments[1].start} -- ${segments[1].text}`);
                    if (this.buffer.length >= 1) console.error(`-- buffer0 -- ${this.buffer[0].start} -- ${this.buffer[0].text}`);
                    if (this.buffer.length === 2) console.error(`-- buffer1 -- ${this.buffer[1].start} -- ${this.buffer[1].text}`);
                    */
                    if (segments?.length) {
                        const segment = segments[segments.length === 1 ? 0 : 1];
                        switch (this.buffer.length) {
                            case 0: this.buffer[0] = segment; break;
                            case 1:
                                if (this.buffer[0].start === segment.start) this.buffer[0] = segment;
                                else this.buffer[1] = segment;
                                break;
                            case 2:
                                if (this.buffer[1].start === segment.start) this.buffer[1] = segment;
                                else { this.buffer[0] = this.buffer[1]; this.buffer[1] = segment; }
                                break;
                            default: break;
                        }


                        /*console.error('------------------ APRES ------------------------');
                        if (this.buffer.length >= 1) console.error(`-- buffer0 -- ${this.buffer[0].start} -- ${this.buffer[0].text}`);
                        if (this.buffer.length === 2) console.error(`-- buffer1 -- ${this.buffer[1].start} -- ${this.buffer[1].text}`);
                        */

                        //const text = (this.buffer[0] ? `${this.buffer[0].text}` : '') + (this.buffer[1] ? `<br/>${this.buffer[1].text}` : '');
                        const text = this.buffer[this.buffer[1] ? 1 : 0].text;
                        //const text = segments[segments.length - 1].text;
                        if (this.roomJid) {
                            const id = ConversationRB.getUniqueMessageId();
                            const message = $msg({ id, from: this.xmppService.jid, to: this.roomJid, type: 'groupchat' });
                            message.c("asr", { xmlns: 'http://jabber.org/asr', text, userId: this.contactService.userContact.dbId });
                            await this.xmppService.send(message);
                        }

                    }
                    else if (data.eod) { this.websocket.close(); }
                }
                else if (this.currentModel.name === 'Gladia') {
                    const utterance = JSON.parse(event.data.toString());
                    if (Object.keys(utterance).length !== 0) {
                        if (ERROR_KEY in utterance) {
                            console.error(`${utterance[ERROR_KEY]}`);
                            this.websocket.close();

                        } else {
                            if (utterance?.[TRANSCRIPTION_KEY]) {
                                const text = utterance[TRANSCRIPTION_KEY];
                                if (this.roomJid) {
                                    const id = ConversationRB.getUniqueMessageId();
                                    const message = $msg({ id, from: this.xmppService.jid, to: this.roomJid, type: 'groupchat' });
                                    message.c("asr", { xmlns: 'http://jabber.org/asr', text, userId: this.contactService.userContact.dbId });
                                    await this.xmppService.send(message);
                                }
                            }
                        }
                    }

                }
                else {
                    if (data.partial || data.text) {
                        const text = data.text ? data.text : data.partial;
                        const partial = !data.text;
                        text.charAt(0).toUpperCase();

                        if (this.roomJid) {
                            const id = ConversationRB.getUniqueMessageId();
                            const message = $msg({ id, from: this.xmppService.jid, to: this.roomJid, type: 'groupchat' });
                            message.c("asr", { xmlns: 'http://jabber.org/asr', text, partial: partial.toString(), userId: this.contactService.userContact.dbId });
                            await this.xmppService.send(message);
                        }
                    }
                    else if (data.eod) { this.websocket.close(); }
                }
            };
        });
    }

    private recorderProcess(event) {
        if (this.muted) return;
        const data = event.inputBuffer.getChannelData(0);
        if (this.currentModel.name.startsWith('Whisper')) this.websocket.send(data);
        else this.websocket.send(this.convertFloat32ToInt16(data));
    }

    private convertFloat32ToInt16(data) {
        let lenght = data.length;
        const buf = new Int16Array(lenght);
        while (lenght--) { buf[lenght] = Math.min(1, data[lenght]) * 0x7FFF; }
        return buf.buffer;
    }

    private onSpeechMessage(stanza: any): boolean {
        try {
            const asrElem = $(stanza).find('asr');
            if (asrElem.length > 0) {
                const messages = this.speechRecognitionMessages.value;
                this.peer = $(stanza).attr("from").split('/')[0];
                console.error('peer' + this.peer);
                const id = asrElem.attr('userId');
                const text = asrElem.attr('text');
                const partial = asrElem.attr('partial') === 'true';
                const index = messages.findIndex((message: SpeechRecognitionMessage) => { return message.id === id; });
                let user = null;
                if (index !== -1) { messages[index].text = text; user = messages[index].user; }
                else {
                    user = this.contactService.getContactFromCacheById(id);
                    messages.push(SpeechRecognitionMessage.create(id, text, user, this));
                }
                this.speechRecognitionMessages.next(messages);
                if (this.transcriptionOn && !partial) {
                    this.transcription.value.addMessage(user, text);
                    this.transcription.next(this.transcription.value);
                }
            }
            return true;
        }
        catch (error) {
            this.logger.error("[speechRecognitionService] onSpeechMessage error " + error);
            return true;
        }
    }

    private subscribeToASRMessages(): void {
        this.logger.info(`[SpeechRecognitionService] - subscribeToASRMessages`);
        if (this.xmppHandler) { this.xmppService.connection.deleteHandler(this.xmppHandler); this.xmppHandler = null; }
        this.xmppHandler = this.xmppService.connection.addHandler((stanza) => { return this.onSpeechMessage(stanza); }, 'http://jabber.org/asr', 'message', null);
    }
}
